import * as moment from 'moment';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BreadcrumbsService } from '../../core/breadcrumbs.service';
import dayGridPlugin from '@fullcalendar/daygrid';
import { ScrollService } from '../../core/scroll.service';
import { LangService } from '../../core/lang.service';
import { IAccountType } from '../../constants/account-types';
import { LoginGuardService } from '../../api/login-guard.service';
import { IInstitutionInfo, MyInstitutionService } from '../my-institution.service';
import { ChecklistStepRoute } from '../pnel-cts-checklist/model/checklist-steps';
import { Subscription } from 'rxjs';
import { MyTestSessionsSetupService, ITestSessionSetupInfo } from '../my-test-sessions-setup.service';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { DemoDataService, IAvailableSession } from '../demo-data.service';
import { AuthService } from '../../api/auth.service';

enum SETUP_STATES {
  LOADING      = 'LOADING',
  NEW_SETUP    = 'NEW_SETUP',
  RESUME_SETUP = 'RESUME_SETUP',
  SETUP_DONE   = 'SETUP_DONE',
}

@Component({
  selector: 'view-dashboard',
  templateUrl: './view-dashboard.component.html',
  styleUrls: ['./view-dashboard.component.scss']
})
export class ViewDashboardComponent implements OnInit, OnDestroy {

  public breadcrumb = [];
  public instInfo:IInstitutionInfo;
  public setupInfo:ITestSessionSetupInfo;
  private subs:Subscription[] = [];
  private SETUP_STATES = SETUP_STATES;

  activeSessions = [];
  upcomingSessions = [];

  // calendar
  @ViewChild('calendar') calendarComponent: FullCalendarComponent;
  calendarPlugins = [dayGridPlugin]; 
  eventTimeFormat: { // like '14:30:00'
    hour: '2-digit',
    minute: '2-digit',
    meridiem: true
  };
  calendarEvents = [/* { title: 'L#1', date: '2019-11-01 09:00', backgroundColor: COLOR_PALETTE[0] }, */];
  constructor(
    private auth: AuthService,
    private myInst: MyInstitutionService,
    private mySetup: MyTestSessionsSetupService,
    private loginGuard: LoginGuardService, // 
    private breadcrumbsService: BreadcrumbsService,
    private scrollService: ScrollService,
    private lang: LangService,
    private router:Router,
    private demoData:DemoDataService,
  ) { }
  
  ngOnInit() {
    this.loginGuard.activate([IAccountType.TEST_ADMIN]);
    this.scrollService.scrollToTop();
    this.breadcrumb = [
      this.breadcrumbsService.TESTADMIN_LANDING(),
      this.breadcrumbsService.TESTADMIN_DASHBOARD(),
    ];
    this.initRouteView()
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe );
  }

  isInited:boolean;
  initRouteView(){
    this.subs = this.subs.concat([
      this.myInst.sub().subscribe(this.updateInstInfo),
      this.mySetup.sub().subscribe(this.updateSetupInfo)
    ]);
    this.auth.user().subscribe(userInfo => {
      if (userInfo && !this.isInited){
        this.isInited = true;
        this.loadAvailableSessions();
      }
    });
  }

  loadAvailableSessions(){
    const sessions = this.demoData.getTestSessions();
    sessions.forEach(session => {
      if (session.__isStartingSoon){
        this.activeSessions.push(session);
      }
      this.upcomingSessions.push(session);
      this.calendarEvents.push({
        title: 'Room' + ' ' + session.room,
        date: session.__dateTimeStartStr
      })
      this.calendarEvents = [].concat(this.calendarEvents);
    })
    
  }

  getPrintRoute(sessionId){
    return  `/${this.lang.c()}/${IAccountType.TEST_ADMIN}/print-session/${sessionId}`
  }
  
  renderLongTimestamp(m:moment.Moment){
    return m.format('h:mma, MMM. D, YYYY (dddd)')
    // 4:30pm, Sept. 22, 2020 (Wednesday)
  }

  renderShortTimestamp(m:moment.Moment){
    return m.format('MMM. D') + ' at ' + m.format('h:mma')
  }

  renderCapacityProportion(session:IAvailableSession){
    return Math.round(100 * session.bookingsCount / session.capacity);
  }

  updateInstInfo = (instInfo:IInstitutionInfo) => {
    this.instInfo = instInfo;
  }

  updateSetupInfo = (setupInfo:ITestSessionSetupInfo) => {
    this.setupInfo = setupInfo;
  }

  getInvigilDisplay(invigilatorId:number){
    const invigil = this.demoData.getInvigilatorBasicInfo(invigilatorId);
    if (invigil){
      let str = invigil.name;
      if (invigil.email){
        str = str + ' ('+invigil.email+')';
      }
      return str;
    }
  }

  createNewTestSessionSetup(){
    this.mySetup
      .create()
      .then( setupId => {
        this.navToFirstCS(setupId);
      })
  }

  toggleAsNewUser(){
    this.demoData.isSimulNewAccount = !this.demoData.isSimulNewAccount;
  }
  isToggledAsNewUser(){
    return this.demoData.isSimulNewAccount;
  }

  getSetupState(){
    if (this.setupInfo){
      if (this.isToggledAsNewUser()){
        return this.SETUP_STATES.NEW_SETUP;
      }
      return SETUP_STATES.SETUP_DONE; // to do : remove temp
      if (this.setupInfo.__reqNew){
        return this.SETUP_STATES.NEW_SETUP;
      }
      else{
        // TO DO : check if sessions have been released yet...
        return this.SETUP_STATES.RESUME_SETUP;
      }
    }
    return this.SETUP_STATES.LOADING;
  }

  navToFirstCS(setupId:number){
    this.navToChecklistScreen(setupId, ChecklistStepRoute.TestWindow);
  }
  navToLastVisitedCS(setupInfo:ITestSessionSetupInfo){
    const setupId = setupInfo.id;
    // not reall the last screen, but good enough for most cases
    if (setupInfo.testWindowId || setupInfo.testWindowId === 0){
      this.navToChecklistScreen(setupId, ChecklistStepRoute.Location);
    }
    else{
      this.navToChecklistScreen(setupId, ChecklistStepRoute.TestWindow);
    }
  }
  navToChecklistScreen(setupId:number, step: ChecklistStepRoute){
    this.router.navigate([
      this.lang.getCurrentLanguage(),
      IAccountType.TEST_ADMIN,
      'create-test-sessions',
      setupId,
      step
    ]);
  }

  isSessionsActive(){
    return false;
  }

  isSessionsUpcoming(){
    return true;
  }

  resumeCreateNewTestSessions(){
    this.navToLastVisitedCS(this.setupInfo);
  }

  getInvigRoute(sessionId:number) {
    return `/${this.lang.getCurrentLanguage()}/${IAccountType.TEST_ADMIN}/invigilate/${sessionId}`;
  }

  getBookingsRoute(sessionId:number) {
    return `/${this.lang.getCurrentLanguage()}/${IAccountType.TEST_ADMIN}/manage-session/${sessionId}`;
  }

}
